"use client";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, MapPin, History, ArrowRight } from "lucide-react";

import { Button } from "@/app/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Badge } from "@/app/components/ui/badge";
import { ZonePicker } from "@/components/shared/zone-picker";
import { SpotPicker } from "@/components/shared/spot-picker";
import { useWorkHistory } from "@/hooks/use-work";
import { cn } from "@/app/components/ui/utils";
import type { WorkHistoryEntry } from "@/lib/types";
import { useOrgTimeZone } from "@/hooks/use-settings";
import { formatDateTimeInTimeZone } from "@/lib/date-utils";

interface WorkHistoryViewProps {
  locationId: number;
  initialZoneId?: number | null;
  initialSpotId?: number | null;
  title?: string;
  showHeader?: boolean;
}

function resultBadgeClass(result: WorkHistoryEntry["result"]) {
  switch (result) {
    case "pass":
      return "bg-green-100 text-green-700 hover:bg-green-100";
    case "fail":
      return "bg-red-100 text-red-700 hover:bg-red-100";
    case "done":
      return "bg-blue-100 text-blue-700 hover:bg-blue-100";
    case "n_a":
      return "bg-gray-100 text-gray-700 hover:bg-gray-100";
    default:
      return "bg-yellow-100 text-yellow-700 hover:bg-yellow-100";
  }
}

function resultLabel(result: WorkHistoryEntry["result"]) {
  switch (result) {
    case "pass":
      return "Pass";
    case "fail":
      return "Fail";
    case "done":
      return "Done";
    case "n_a":
      return "N/A";
    default:
      return result;
  }
}

export function WorkHistoryView({
  locationId,
  initialZoneId,
  initialSpotId,
  title,
  showHeader = true,
}: WorkHistoryViewProps) {
  const timeZone = useOrgTimeZone();
  const navigate = useNavigate();
  const [zoneId, setZoneId] = useState<number | null>(initialZoneId ?? null);
  const [spotId, setSpotId] = useState<number | null>(initialSpotId ?? null);

  useEffect(() => {
    setZoneId(null);
    setSpotId(null);
  }, [locationId]);

  useEffect(() => {
    setZoneId(initialZoneId ?? null);
    setSpotId(initialSpotId ?? null);
  }, [initialZoneId, initialSpotId]);

  const filters = {
    ...(zoneId ? { zone: zoneId } : {}),
    ...(spotId ? { spot: spotId } : {}),
    page_size: 50,
  };

  const { data, isLoading } = useWorkHistory(filters, !!zoneId || !!spotId);

  const entries = data?.results ?? [];

  return (
    <div className="space-y-4">
      {showHeader && (
        <div className="flex items-center gap-2">
          <History className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold">{title ?? "Work history"}</h3>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label className="text-sm font-medium">Zone</label>
          <ZonePicker
            locationId={locationId}
            value={zoneId}
            onChange={(id) => {
              setZoneId(id);
              setSpotId(null);
            }}
            placeholder="Pick a zone or node"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Spot (optional)</label>
          <SpotPicker
            locationId={locationId}
            zoneId={zoneId}
            value={spotId}
            onChange={setSpotId}
            placeholder={zoneId ? "Filter by spot" : "Select a zone first"}
            disabled={!zoneId}
          />
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading history...
        </div>
      )}

      {!isLoading && (zoneId || spotId) && entries.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            No completed work history at this location.
          </CardContent>
        </Card>
      )}

      {!isLoading && entries.length > 0 && (
        <div className="space-y-3">
          {entries.map((entry) => (
            <Card key={entry.id}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <CardTitle className="text-base">{entry.task_title}</CardTitle>
                    <div className="text-sm text-muted-foreground">{entry.project_name}</div>
                  </div>
                  <Badge variant="secondary" className={cn(resultBadgeClass(entry.result))}>
                    {resultLabel(entry.result)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 pt-0">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  {entry.zone_path || "—"}
                  {entry.spot_name ? ` / ${entry.spot_name}` : null}
                </div>
                {entry.notes ? (
                  <p className="text-sm text-muted-foreground">{entry.notes}</p>
                ) : null}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    Completed{' '}
                    {entry.completed_at
                      ? formatDateTimeInTimeZone(entry.completed_at, timeZone)
                      : "—"}
                    {entry.completed_by_username ? ` by ${entry.completed_by_username}` : ""}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2"
                    onClick={() => navigate(`/work/run/${entry.task}`)}
                  >
                    View task
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
